import {
  PROJECT_COLOR_MENU_ID,
  ProjectColorMenu,
  ToolbarAnchor,
  WireframesMenu,
} from '@components/ArtBoard/ArtboardToolbar'
import type { BlockWireframe, ID } from '@octopus-do/octopus-project-core'
import { Toolbar } from '@octopus-do/octopus-storybook'
import {
  Image,
  ImageSlash,
  LinkSlash,
  Trash,
} from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { useRef, useState } from 'react'

import { createSwitch } from '@/utils'

// The sidebar is not scaled: the anchor keeps the plate 12px above the card.
const SIDEBAR_SCALE = 1

interface Props {
  // The card element: the wireframes popover sits to its left.
  anchorRef: React.RefObject<HTMLDivElement | null>
  colorId: ID
  deleteLabel: string
  // The buttons stay in place and do nothing.
  disabled?: boolean
  hasImage: boolean
  // A project symbol taken from the library can be unlinked from it.
  linked?: boolean
  onColor: (colorId: ID) => void
  onDelete: () => void
  onImage: (file: File) => void
  onRemoveImage: () => void
  onUnlink?: () => void
  // The card's wireframes as a whole after a pick or Clear in the menu.
  onWireframesChange: (wireframes: BlockWireframe[]) => void
  // The picked file is on its way: the image button shows a loader
  uploading?: boolean
  wireframes: BlockWireframe[]
}

// The toolbar of a selected symbol card (figma toolbar type=symbollibrary,
// 86161:861838): colour, wireframes, image | unlink, delete.
export const SymbolToolbar: React.FC<Props> = ({
  anchorRef,
  colorId,
  deleteLabel,
  disabled = false,
  hasImage,
  linked = false,
  onColor,
  onDelete,
  onImage,
  onRemoveImage,
  onUnlink = () => {},
  onWireframesChange,
  uploading = false,
  wireframes,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  // The wireframes popover stands in for the toolbar while it is open.
  const [wireframesOpen, setWireframesOpen] = useState(false)

  const onImageClick = () => {
    if (hasImage) {
      onRemoveImage()
      return
    }

    inputRef.current?.click()
  }

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.item(0)

    if (file && e.target.files?.length === 1) {
      onImage(file)
    }
  }

  const switcher = createSwitch()
    .case([PROJECT_COLOR_MENU_ID], onColor)
    .case(['image'], onImageClick)
    .case(['unlink'], onUnlink)
    .case(['delete'], onDelete)

  const onClick = (keys: string[]) => switcher.run(keys)

  return (
    <ToolbarAnchor hidden={wireframesOpen} scale={SIDEBAR_SCALE}>
      <Toolbar.Root onClick={onClick}>
        <ProjectColorMenu colorId={colorId} disabled={disabled} />
        <WireframesMenu
          anchorRef={anchorRef}
          disabled={disabled}
          onChange={onWireframesChange}
          onClose={() => setWireframesOpen(false)}
          onOpen={() => setWireframesOpen(true)}
          side="left"
          wireframes={wireframes}
        />
        <Toolbar.Button
          disabled={disabled}
          icon={hasImage ? ImageSlash : Image}
          id="image"
          loading={uploading}
          tooltip={hasImage ? 'Remove image' : 'Upload image'}
          variant={hasImage ? 'danger' : 'default'}
        />
        <Toolbar.Divider />
        {linked && (
          <Toolbar.Button
            disabled={disabled}
            icon={LinkSlash}
            id="unlink"
            tooltip="Unlink from library"
          />
        )}
        <Toolbar.Button
          disabled={disabled}
          icon={Trash}
          id="delete"
          tooltip={deleteLabel}
          variant="danger"
        />
      </Toolbar.Root>

      <input
        accept="image/*"
        hidden
        name="file"
        onChange={onFile}
        ref={inputRef}
        type="file"
      />
    </ToolbarAnchor>
  )
}
